import {
  DayPickerStrings,
  FlexSpinner,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { FieldProps } from 'formik'
import { FormikDropdown } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { Proving } from 'model'
import { DayOfWeek, IDropdownOption, ITag, Label } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ParticipantListItem } from 'views/ParticipantViews'
import { useAccomodationsQuery } from 'views/ProvingDetailsView/hooks'
import * as Yup from 'yup'

export type ProvingDetailsAccomodationFormValues = Proving

export class ProvingDetailsAccomodationFormSchema {
  constructor() {
    return Yup.object().shape({
      accomodation: Yup.object().shape({
        accomodationType: Yup.string(),
        accomodation: Yup.string()
          .nullable()
          .when('accomodationType', {
            is: (type) => ['accomodationWithPreference'].includes(type),
            then: Yup.string().required(
              'Unterkunft auswählen ist ein Pflichtfeld'
            ),
          }),
        dateFrom: Yup.date().when('accomodationType', {
          is: (type) => ['accomodationWithPreference'].includes(type),
          then: Yup.date().required(),
        }),
        dateTo: Yup.date().when('accomodationType', {
          is: (type) => ['accomodationWithPreference'].includes(type),
          then: Yup.date()
            .required()
            .when(
              'dateFrom',
              (dateFrom: any, schema: any) => dateFrom && schema.min(dateFrom)
            ),
        }),
      }),
    })
  }
}

export interface ProvingDetailsLodgingValues {
  Lodging: string
}

export const ProvingDetailsAccomodationForm: React.FunctionComponent<{
  proving: Partial<Proving>
  notification?: boolean
  participant?: Partial<ParticipantListItem>
  wizardParticipants?: any
}> = ({ proving, notification, participant, wizardParticipants }) => {
  const { t } = useTranslation()
  const [selectedItem, setSelectedItem] = React.useState()
  const { data, loading, error } = useAccomodationsQuery({
    variables: { provingGroundId: proving.provingGround!._id },
    fetchPolicy: 'network-only',
  })
  let wizardParticipant
  if (wizardParticipants) {
    wizardParticipant =
      proving &&
      proving.participants &&
      proving.participants.find((x) => x._id === wizardParticipants[0])
  }

  const dateFromToUse = wizardParticipant
    ? wizardParticipant.dateFrom
    : proving.dateFrom
  const dateToToUse = wizardParticipant
    ? wizardParticipant.dateTo
    : proving.dateTo

  const accomodations: ITag[] =
    data && !loading
      ? data.resources.map((resource) => ({
          key: resource._id,
          name: resource.title,
        }))
      : []

  const options: IDropdownOption[] = [
    { key: 'noAccomodation', text: t('dropdown.option.noAccomodation') },
    {
      key: 'accomodationWithPreference',
      text: t('dropdown.option.accomodationWithPreference'),
    },
  ]

  // const validateDateFrom = () => {
  //   if (participant) {
  //     return new Date(participant.provingDateFrom as any)
  //   } else {
  //     return new Date(proving.dateFrom)
  //   }
  // }

  // const validateDateTo = () => {
  //   if (participant) {
  //     return new Date(participant.provingDateTo as any)
  //   } else {
  //     return new Date(proving.dateTo)
  //   }
  // }

  return (
    <FormStack>
      {notification && (
        <FieldStack>
          <Label>
            {t(
              'Achtung! Es sind mehrere Einträge ausgewählt! Die Werte werden bei allen ausgewählten Einträgen mit den Werten aus der Formularmaske überschrieben!'
            )}
          </Label>
        </FieldStack>
      )}
      <FieldStack>
        <FieldStackItem
          name="accomodation.accomodationType"
          isRequired={true}
          render={(fieldProps: FieldProps<ProvingDetailsLodgingValues>) => {
            setSelectedItem(fieldProps.field.value)
            return (
              <FormikDropdown
                label={t('field.accomodation.label')}
                onChanged={(option: any) => {
                  setSelectedItem(option.key)
                  if (option.key === 'noAccomodation') {
                    fieldProps.form.setFieldValue(
                      'accomodation.accomodation',
                      null
                    )
                  } else {
                    fieldProps.form.setFieldValue(
                      'accomodation.dateFrom',
                      dateFromToUse
                    )
                    fieldProps.form.setFieldValue(
                      'accomodation.dateTo',
                      dateToToUse
                    )
                  }
                }}
                {...fieldProps}
                options={options}
              />
            )
          }}
        />
      </FieldStack>

      <FlexSpinner loading={loading} error={error}>
        {selectedItem === 'accomodationWithPreference' && (
          <FieldStack>
            <FieldStackItem
              name="accomodation.accomodation"
              render={(fieldProps: FieldProps<ProvingDetailsLodgingValues>) => {
                return (
                  <FormikTagPicker
                    label={t('Unterkunft auswählen')}
                    {...fieldProps}
                    tags={accomodations}
                  />
                )
              }}
            />
          </FieldStack>
        )}
      </FlexSpinner>
      {['noPreference', 'accomodationWithPreference'].includes(
        selectedItem
      ) && (
        <FieldStack horizontal={true}>
          <FieldStackItem
            name="accomodation.dateFrom"
            label={t('field.dateFrom.label')}
            component={FormikGraphQLDatePicker}
            // minDate={validateDateFrom()}
            // maxDate={validateDateTo()}
            firstDayOfWeek={DayOfWeek.Monday}
            strings={DayPickerStrings}
            showWeekNumbers={true}
            formatDate={onFormatDate}
            initialPickerDate={new Date(dateFromToUse)}
          />
          <FieldStackItem
            name="accomodation.dateTo"
            label={t('field.dateTo.label')}
            component={FormikGraphQLDatePicker}
            // minDate={validateDateFrom()}
            // maxDate={validateDateTo()}
            firstDayOfWeek={DayOfWeek.Monday}
            strings={DayPickerStrings}
            showWeekNumbers={true}
            formatDate={onFormatDate}
            initialPickerDate={new Date(dateToToUse)}
          />
        </FieldStack>
      )}
    </FormStack>
  )
}
